// Заливка промрайонов тоннами квоты выбранного вида.
//
// Квота выдаётся на код ССД, а лист карты бывает подзоной зоны с квотой или
// подзоной, чью долю квота делит (`src/cabinet/quotas.py::zone_of_leaf`):
// какой лист красят какие районы квоты, отвечает СЕРВЕР (`districts` ответа),
// а здесь тонны только складываются и нормируются. Цвет — тон слоя
// промрайонов бренда, сила — прозрачностью: новый цвет на карте читался бы
// как новый слой.
//
// Контур есть у КАЖДОГО района с квотой, а не только у выбранного: соседние
// районы одного тона (264 и 265 в Беринговом море) без него сливаются в одно
// пятно, и границу между ними было видно лишь после выбора. Контуры всех
// промрайонов (тумблер `fd`) по-прежнему выключены — здесь только районы квоты.

import type maplibregl from 'maplibre-gl'
import type { DistrictCollection } from '../api'
import { districtColor } from '../map/districts'
import { addUnderBoundaries } from '../map/layerOrder'

export const QUOTA_FILL = 'quota-fill'
export const QUOTA_EDGE = 'quota-edge'
export const QUOTA_LINE = 'quota-selected'
const SOURCE = 'quota-districts'

/** Листья карты с тоннами квоты: `share` 0…1 от самого крупного района. */
export function shadeDistricts(
  fc: DistrictCollection | null | undefined,
  mapping: Record<string, number[]>,
  tonsByZone: Map<number, number>,
  selected: number | null,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  if (!fc) return { type: 'FeatureCollection', features }
  const tonsOf = (code: string) =>
    (mapping[code] ?? []).reduce((s, z) => s + (tonsByZone.get(z) ?? 0), 0)
  const max = Math.max(0, ...fc.features.map(f => tonsOf(f.properties.code)))
  for (const f of fc.features) {
    const zones = mapping[f.properties.code] ?? []
    const tons = tonsOf(f.properties.code)
    if (!zones.length || tons <= 0) continue
    features.push({
      type: 'Feature',
      geometry: f.geometry,
      properties: {
        code: f.properties.code,
        zone: zones[0],
        tons,
        share: max > 0 ? tons / max : 0,
        selected: selected != null && zones.includes(selected),
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export function applyQuotaLayer(map: maplibregl.Map, data: GeoJSON.FeatureCollection): void {
  const src = map.getSource(SOURCE) as maplibregl.GeoJSONSource | undefined
  if (src) {
    src.setData(data)
    return
  }
  map.addSource(SOURCE, { type: 'geojson', data })
  const color = districtColor()
  addUnderBoundaries(map, {
    id: QUOTA_FILL,
    type: 'fill',
    source: SOURCE,
    paint: {
      'fill-color': color,
      'fill-opacity': ['interpolate', ['linear'], ['get', 'share'], 0, 0.08, 1, 0.55],
    },
  })
  addUnderBoundaries(map, {
    id: QUOTA_EDGE,
    type: 'line',
    source: SOURCE,
    paint: { 'line-color': color, 'line-width': 1, 'line-opacity': 0.9 },
  })
  addUnderBoundaries(map, {
    id: QUOTA_LINE,
    type: 'line',
    source: SOURCE,
    filter: ['==', ['get', 'selected'], true],
    paint: { 'line-color': color, 'line-width': 3 },
  })
}
